import React, { useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Box, Grid, Paper, Stack, Grid2 } from "@mui/material";
import { useShopSlice, setCheckoutStatus } from "@store/slices/shop";
import { triggerNotification } from "@store/slices/notification";
import { useDispatch } from "react-redux";
import { useQuery } from "@apollo/client";
import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { SEAT_DISPLAY } from "../../../utils/seatConstants";
import { useSubscriptionAccess } from "@company/hooks/useSubscriptionAccess";
import type { Product } from "@/utils/seatUtils";
import { SEAT_UPGRADE_PREVIEW } from "@shop/graphql/queries/seatUpgradePreview";

interface CheckoutConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
}

const CheckoutConfirmationDialog: React.FC<CheckoutConfirmationDialogProps> = ({ open, onClose, onConfirm, isLoading }) => {
    const dispatch = useDispatch();
    const { selectedProducts, products, checkoutStatus } = useShopSlice();

    // Use enhanced subscription access hook instead of direct status checking
    const subscriptionAccess = useSubscriptionAccess();

    // Fetch current subscription info to calculate additional seats
    const { data: subscriptionData } = useQuery(FETCH_SUBSCRIPTION_INFO, {
        errorPolicy: "ignore",
    });

    const currentSeats = subscriptionData?.subscriptionInfo;

    // Total desired seats after user selection
    const desiredSeats = React.useMemo(() => {
        const seatCounts = { S: currentSeats?.seatsS || 0, M: currentSeats?.seatsM || 0, L: currentSeats?.seatsL || 0 };
        selectedProducts.forEach(({ priceId, quantity }) => {
            // Determine the seatType directly from the price information
            const seatType = products
                .flatMap(p => p.prices)
                .find(p => p.priceId === priceId)?.seatType as 'S' | 'M' | 'L' | null;

            if (!seatType) return; // skip non-seat prices

            seatCounts[seatType] = quantity;
        });
        return seatCounts;
    }, [selectedProducts, currentSeats, products]);

    // GraphQL preview query (skipped if not logged-in subscription scenario)
    const { data: previewData } = useQuery(SEAT_UPGRADE_PREVIEW, {
        variables: {
            subscriptionId: subscriptionData?.subscriptionInfo?.id || "",
            seatsS: desiredSeats.S,
            seatsM: desiredSeats.M,
            seatsL: desiredSeats.L,
        },
        skip: !subscriptionAccess.hasAccess || !subscriptionData?.subscriptionInfo?.id,
    });

    useEffect(() => {
        if (checkoutStatus === "success") {
            dispatch(triggerNotification({ title: "Erfolgreich", content: "Sitz-Updates erfolgreich abgeschlossen", type: "success", autoHideDuration: 3000 }));
            onClose();
            // Reset status to prevent repeated notifications on subsequent visits
            dispatch(setCheckoutStatus("uninitialized"));
        }
    }, [checkoutStatus, dispatch, onClose]);

    // Function to find product and price details by priceId (hoisted declaration avoids TDZ)
    function getProductDetails(priceId: string) {
        for (const product of products) {
            const price = product.prices.find((p) => p.priceId === priceId);
            if (price) {
                return {
                    name: product.name,
                    description: product.description,
                    price: price.amount,
                    currency: price.currency,
                    interval: price.interval,
                    seatType: price.seatType,
                };
            }
        }
        return null;
    }

    // Get current seats for a specific seat type
    const getCurrentSeatsForType = (seatType: 'S' | 'M' | 'L' | null): number => {
        if (!subscriptionAccess.hasAccess || !currentSeats || !seatType) return 0;
        
        if (seatType === 'S') return currentSeats.seatsS || 0;
        if (seatType === 'M') return currentSeats.seatsM || 0;
        if (seatType === 'L') return currentSeats.seatsL || 0;
        
        return 0;
    };

    // Get display information for a product (handles seat vs non-seat products differently)
    const getDisplayInfo = (priceId: string, quantity: number) => {
        const details = getProductDetails(priceId);
        if (!details) return null;

        const seatType = details.seatType as 'S' | 'M' | 'L' | null;
        const isSeat = seatType !== null;
        
        if (isSeat && subscriptionAccess.hasAccess) {
            // For existing subscription seats: show additional seats
            const currentSeatsCount = getCurrentSeatsForType(seatType);
            const additionalSeats = Math.max(0, quantity - currentSeatsCount);
            
            // Calculate adjusted upgrade cost if seats are being added
            let adjustedTotal = details.price * additionalSeats;

            if (previewData?.seatUpgradePreview) {
                if (subscriptionAccess.isInTrial) {
                    adjustedTotal = 0; // free during trial per backend
                } else {
                    const breakdownLine = previewData.seatUpgradePreview.breakdown.find((l:any)=>{
                        return l.seatType === seatType;
                    });
                    if (breakdownLine) adjustedTotal = breakdownLine.total;
                }
            }
            
            const displayName = seatType ? SEAT_DISPLAY[seatType] : details.name;
            
            return {
                ...details,
                displayQuantity: additionalSeats,
                displayName: `${displayName} (zusätzlich)`,
                displayDescription: `${additionalSeats} zusätzliche Sitze (aktuell: ${currentSeatsCount})`,
                displayPrice: details.price * additionalSeats,
                adjustedPrice: adjustedTotal,
                isAdditionalSeat: true
            };
        } else {
            // For new subscriptions or non-seat products: show total quantity
            const displayName = seatType ? SEAT_DISPLAY[seatType] : details.name;
            
            return {
                ...details,
                displayQuantity: quantity,
                displayName: displayName,
                displayDescription: details.description,
                displayPrice: details.price * quantity,
                adjustedPrice: null,
                isAdditionalSeat: false
            };
        }
    };

    // Calculate total
    const calculateTotal = () => {
        let total = 0;
        selectedProducts.forEach(({ priceId, quantity }) => {
            const displayInfo = getDisplayInfo(priceId, quantity);
            if (displayInfo) {
                total += (displayInfo.adjustedPrice !== null ? displayInfo.adjustedPrice : displayInfo.displayPrice);
            }
        });
        return total;
    };

    // Format currency
    const formatCurrency = (amount: number, currency: string) => {
        // Convert from cents to euros (divide by 100)
        const amountInEuros = amount / 100;
        return new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: currency || "EUR",
        }).format(amountInEuros);
    };

    // Translate interval to German
    const translateInterval = (interval: string): string => {
        switch (interval) {
            case "month":
                return "Monat";
            case "year":
                return "Jahr";
            default:
                return interval;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Bestellung bestätigen</DialogTitle>
            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    Bitte überprüfen Sie Ihre ausgewählten Produkte:
                </Typography>

                {/* Enhanced subscription status indicator */}
                {subscriptionAccess.isInGracePeriod && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="warning.main">
                            ⚠️ Ihr Abonnement befindet sich in der Kulanzfrist. Diese Änderungen werden aktiv, sobald Ihre Zahlungsinformationen aktualisiert sind.
                        </Typography>
                    </Box>
                )}

                {/* Trial period indicator for seat upgrades */}
                {subscriptionAccess.isInTrial && selectedProducts.some(({ priceId }) => {
                    const productDetails = getProductDetails(priceId);
                    return productDetails?.seatType !== null;
                }) && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="info.dark">
                            ✨ <strong>Testphase aktiv:</strong> Sitz-Upgrades sind während der Testphase kostenlos. 
                            Die vollen Kosten werden mit Ihrer ersten Jahresrechnung nach Ende der Testphase berechnet.
                        </Typography>
                    </Box>
                )}

                <Paper variant="outlined" sx={{ mt: 2, mb: 3, p: 1 }}>
                    {/* Header row */}
                    <Grid2 container sx={{ p: 2, bgcolor: "background.default" }}>
                        <Grid2 size={5}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Produkt
                            </Typography>
                        </Grid2>
                        <Grid2 size={2} sx={{ textAlign: "center" }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Preis
                            </Typography>
                        </Grid2>
                        <Grid2 size={2} sx={{ textAlign: "center" }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Menge
                            </Typography>
                        </Grid2>
                        <Grid2 size={3} sx={{ textAlign: "right" }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Gesamt
                            </Typography>
                        </Grid2>
                    </Grid2>

                    <Divider />

                    {/* Product rows */}
                    {selectedProducts
                        .filter(({ quantity }) => quantity > 0) // Only show products with quantity > 0
                        .map(({ priceId, quantity }) => {
                        const displayInfo = getDisplayInfo(priceId, quantity);
                        if (!displayInfo) return null;

                        // Skip showing products with 0 additional seats
                        if (displayInfo.isAdditionalSeat && displayInfo.displayQuantity === 0) {
                            return null;
                        }

                        return (
                            <React.Fragment key={priceId}>
                                <Grid2 container sx={{ p: 2 }} alignItems="center">
                                    <Grid2 size={5}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body1" fontWeight="medium">
                                                {displayInfo.displayName}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {displayInfo.displayDescription}
                                            </Typography>
                                        </Stack>
                                    </Grid2>
                                    <Grid2 size={2} sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {displayInfo.adjustedPrice !== null && displayInfo.adjustedPrice !== displayInfo.displayPrice ? (
                                                <>
                                                    <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 0.5 }}>
                                                        {formatCurrency(displayInfo.displayPrice, displayInfo.currency)}
                                                    </Box>
                                                    <Box component="span" sx={{ color: 'warning.dark', fontWeight: 'bold' }}>
                                                        {formatCurrency(displayInfo.adjustedPrice, displayInfo.currency)}
                                                    </Box>
                                                </>
                                            ) : (
                                                formatCurrency(displayInfo.displayPrice, displayInfo.currency)
                                            )}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={2} sx={{ textAlign: "center" }}>
                                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                            {displayInfo.isAdditionalSeat ? `+${displayInfo.displayQuantity}` : displayInfo.displayQuantity}
                                        </Typography>
                                    </Grid2>
                                    <Grid2 size={3} sx={{ textAlign: "right" }}>
                                        <Typography variant="body1" fontWeight="medium">
                                            {formatCurrency(displayInfo.displayPrice, displayInfo.currency)}
                                        </Typography>
                                    </Grid2>
                                </Grid2>
                                <Divider />
                            </React.Fragment>
                        );
                    })}

                    {/* Total row */}
                    <Box sx={{ p: 2, textAlign: "right", bgcolor: "background.default" }}>
                        <Typography variant="h6" fontWeight="bold">
                            Gesamtsumme: {formatCurrency(calculateTotal(), "EUR")}
                        </Typography>
                    </Box>
                </Paper>

                <Typography variant="body2" color="text.secondary">
                    Durch das Bestätigen stimmen Sie den Geschäftsbedingungen zu und autorisieren die Zahlung.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Abbrechen
                </Button>
                <Button onClick={onConfirm} variant="contained" disabled={isLoading}>
                    {isLoading ? "Wird verarbeitet..." : "Bestätigen & Bezahlen"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CheckoutConfirmationDialog;
